import {REALTIME_DB} from '@/config/firebase';
import store from '@/redux/store';
import {INoteInfo, ICommentInfo, ECommentStatus} from '@/types/airport';
import {ref, push, set, update, remove, get} from 'firebase/database';

/**
 * Creates a new airport note in Firebase Realtime Database
 * @param icaoCode - Airport ICAO code
 * @param noteData - Note data without ID
 * @returns Promise<string> - The generated note ID
 */
export const createNoteForAirportNote = async (
  icaoCode: string,
  noteData: Omit<INoteInfo, 'id'>
): Promise<string> => {
  const airline = store.getState().user.airline;
  try {
    const notesRef = ref(
      REALTIME_DB,
      `/airport_notes/${airline}/notes/${icaoCode}`
    );
    const newNoteRef = push(notesRef);

    if (!newNoteRef.key) {
      throw new Error('Failed to generate note ID');
    }

    const noteWithId: INoteInfo = {
      ...noteData,
      id: newNoteRef.key,
      created_at: new Date().toISOString()
    };

    await set(newNoteRef, noteWithId);
    console.log('Note created successfully with ID:', newNoteRef.key);

    return newNoteRef.key;
  } catch (error) {
    console.error('Error creating note:', error);
    throw error;
  }
};

/**
 * Updates an existing airport note in Firebase Realtime Database
 * @param icaoCode - Airport ICAO code
 * @param noteId - Note ID to update
 * @param noteData - Partial note data to update
 * @returns Promise<void>
 */
export const updateNoteForAirportNote = async (
  icaoCode: string,
  noteId: string,
  noteData: Partial<INoteInfo>
): Promise<void> => {
  try {
    const airline = store.getState().user.airline;
    const noteRef = ref(
      REALTIME_DB,
      `/airport_notes/${airline}/notes/${icaoCode}/${noteId}`
    );

    const updateData = {
      ...noteData
    };

    await update(noteRef, updateData);
    console.log('Note updated successfully:', noteId);
  } catch (error) {
    console.error('Error updating note:', error);
    throw error;
  }
};

/**
 * Deletes an airport note from Firebase Realtime Database
 * @param icaoCode - Airport ICAO code
 * @param noteId - Note ID to delete
 * @returns Promise<void>
 */
export const deleteNoteForAirportNote = async (
  icaoCode: string,
  noteId: string
): Promise<void> => {
  try {
    const airline = store.getState().user.airline;
    const noteRef = ref(
      REALTIME_DB,
      `/airport_notes/${airline}/notes/${icaoCode}/${noteId}`
    );
    await remove(noteRef);
    console.log('Note deleted successfully:', noteId);
  } catch (error) {
    console.error('Error deleting note:', error);
    throw error;
  }
};

// Comment API Functions

export const getNoteById = async (
  airportCode: string,
  noteId: string
): Promise<INoteInfo> => {
  const airline = store.getState().user.airline;
  const noteRef = ref(
    REALTIME_DB,
    `/airport_notes/${airline}/notes/${airportCode}/${noteId}`
  );
  const snapshot = await get(noteRef);
  return snapshot.val() as INoteInfo;
};

/**
 * Creates a new comment for an airport note
 * @param icaoCode - Airport ICAO code
 * @param noteId - Note ID to comment on
 * @param commentData - Comment data without ID
 * @returns Promise<string> - The generated comment ID
 */
export const createCommentForNote = async (
  icaoCode: string,
  noteId: string,
  commentData: Omit<ICommentInfo, 'id'>
): Promise<string> => {
  try {
    const airline = store.getState().user.airline;
    const commentsRef = ref(
      REALTIME_DB,
      `/airport_notes/${airline}/comments/${icaoCode}/${noteId}`
    );
    const newCommentRef = push(commentsRef);

    if (!newCommentRef.key) {
      throw new Error('Failed to generate comment ID');
    }

    const commentWithId: ICommentInfo = {
      ...commentData,
      id: newCommentRef.key,
      created_at: new Date().toISOString(),
      status: ECommentStatus.PENDING
    };

    await set(newCommentRef, commentWithId);
    console.log('Comment created successfully with ID:', newCommentRef.key);

    return newCommentRef.key;
  } catch (error) {
    console.error('Error creating comment:', error);
    throw error;
  }
};

/**
 * Updates an existing comment
 * @param noteId - Note ID
 * @param commentId - Comment ID to update
 * @param commentData - Partial comment data to update
 * @returns Promise<void>
 */
export const updateCommentForNote = async (
  noteId: string,
  commentId: string,
  commentData: Partial<ICommentInfo>
): Promise<void> => {
  try {
    const airline = store.getState().user.airline;
    const commentRef = ref(
      REALTIME_DB,
      `/airport_notes/${airline}/comments/${noteId}/${commentId}`
    );
    await update(commentRef, commentData);
    console.log('Comment updated successfully:', commentId);
  } catch (error) {
    console.error('Error updating comment:', error);
    throw error;
  }
};

/**
 * Deletes a comment and updates the note's comment count
 * @param icaoCode - Airport ICAO code
 * @param noteId - Note ID
 * @param commentId - Comment ID to delete
 * @returns Promise<void>
 */
export const deleteCommentForNote = async (
  icaoCode: string,
  noteId: string,
  commentId: string
): Promise<void> => {
  try {
    // Delete the comment
    const airline = store.getState().user.airline;
    const commentRef = ref(
      REALTIME_DB,
      `/airport_notes/${airline}/comments/${noteId}/${commentId}`
    );
    await remove(commentRef);

    // Get current note to retrieve comment count
    const noteRef = ref(
      REALTIME_DB,
      `/airport_notes/${airline}/notes/${icaoCode}/${noteId}`
    );
    const noteSnapshot = await get(noteRef);

    if (noteSnapshot.exists()) {
      const noteData = noteSnapshot.val() as INoteInfo;
      const currentCommentCount = noteData.comment_count || 0;

      // Update note's comment count (decrement by 1, but don't go below 0)
      const newCommentCount = Math.max(0, currentCommentCount - 1);
      await update(noteRef, {comment_count: newCommentCount});

      console.log(
        'Comment deleted successfully and note comment count updated:',
        commentId,
        'New count:',
        newCommentCount
      );
    } else {
      console.warn(
        'Note not found when trying to update comment count:',
        noteId
      );
    }
  } catch (error) {
    console.error('Error deleting comment:', error);
    throw error;
  }
};

/**
 * Approves a comment (admin action)
 * @param noteId - Note ID
 * @param commentId - Comment ID to approve
 * @param approvedBy - User ID who approved the comment
 * @returns Promise<void>
 */
export const approveComment = async (
  noteId: string,
  commentId: string,
  approvedBy: string
): Promise<void> => {
  try {
    const updateData = {
      status: ECommentStatus.APPROVED,
      approved_by: approvedBy,
      approved_at: new Date().toISOString()
    };

    await updateCommentForNote(noteId, commentId, updateData);
    console.log('Comment approved successfully:', commentId);
  } catch (error) {
    console.error('Error approving comment:', error);
    throw error;
  }
};

/**
 * Rejects a comment (admin action)
 * @param noteId - Note ID
 * @param commentId - Comment ID to reject
 * @param rejectedBy - User ID who rejected the comment
 * @returns Promise<void>
 */
export const rejectComment = async (
  noteId: string,
  commentId: string,
  rejectedBy: string
): Promise<void> => {
  try {
    const updateData = {
      status: ECommentStatus.REJECTED,
      rejected_by: rejectedBy,
      rejected_at: new Date().toISOString()
    };

    await updateCommentForNote(noteId, commentId, updateData);
    console.log('Comment rejected successfully:', commentId);
  } catch (error) {
    console.error('Error rejecting comment:', error);
    throw error;
  }
};

/**
 * Gets comments for a specific note from Firebase Realtime Database
 * @param icaoCode - Airport ICAO code
 * @param noteId - Note ID to get comments for
 * @returns Promise<ICommentInfo[]> - Array of comments
 */
export const getCommentsByNoteId = async (
  noteId: string
): Promise<ICommentInfo[]> => {
  try {
    const airline = store.getState().user.airline;
    const commentsRef = ref(
      REALTIME_DB,
      `/airport_notes/${airline}/comments/${noteId}`
    );
    const snapshot = await get(commentsRef);

    if (snapshot.exists()) {
      const commentsData = snapshot.val();
      const comments: ICommentInfo[] = [];

      for (const [key, value] of Object.entries(commentsData)) {
        const comment = value as ICommentInfo;
        comments.push({
          ...comment,
          id: key
        });
      }

      return comments;
    } else {
      return [];
    }
  } catch (error) {
    console.error('Error getting comments:', error);
    throw error;
  }
};

/**
 * Gets all notes for a specific airport by ICAO code
 * @param icaoCode - Airport ICAO code
 * @returns Promise<INoteInfo[]> - Array of notes
 */
export const getNotesByAirport = async (
  icaoCode: string
): Promise<INoteInfo[]> => {
  try {
    const airline = store.getState().user.airline;
    const notesRef = ref(
      REALTIME_DB,
      `/airport_notes/${airline}/notes/${icaoCode}`
    );
    const snapshot = await get(notesRef);

    if (!snapshot.exists()) {
      return [];
    }

    const notesData = snapshot.val();
    const notes: INoteInfo[] = [];

    for (const [key, value] of Object.entries(notesData)) {
      const note = value as INoteInfo;
      notes.push({
        ...note,
        id: key
      });
    }

    return notes.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  } catch (error) {
    console.error('Error getting notes for airport:', error);
    throw error;
  }
};

/**
 * Gets all notes for all airports
 * @returns Promise<{ [icaoCode: string]: INoteInfo[] }> - Object mapping ICAO codes to arrays of notes
 */
export const getAllNotesForAllAirports = async (): Promise<
  Record<string, INoteInfo[]>
> => {
  try {
    const airline = store.getState().user.airline;
    const notesRef = ref(REALTIME_DB, `/airport_notes/${airline}/notes`);
    const snapshot = await get(notesRef);

    if (!snapshot.exists()) {
      return {};
    }

    const allNotesData = snapshot.val();
    const allNotes: Record<string, INoteInfo[]> = {};

    for (const [icaoCode, airportNotes] of Object.entries(allNotesData)) {
      const notes: INoteInfo[] = [];

      if (airportNotes && typeof airportNotes === 'object') {
        for (const [noteId, noteData] of Object.entries(airportNotes)) {
          const note = noteData as INoteInfo;
          notes.push({
            ...note,
            id: noteId
          });
        }

        // Sort notes by creation date (newest first)
        allNotes[icaoCode] = notes.sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      }
    }

    return allNotes;
  } catch (error) {
    console.error('Error getting all notes for all airports:', error);
    throw error;
  }
};

/**
 * Gets all comments for all airports
 * @returns Promise<{ [icaoCode: string]: { [noteId: string]: ICommentInfo[] } }> - Nested object of comments
 */
export const getAllCommentsForAllAirports = async (): Promise<
  Record<string, Record<string, ICommentInfo[]>>
> => {
  try {
    const airline = store.getState().user.airline;
    const commentsRef = ref(REALTIME_DB, `/airport_notes/${airline}/comments`);
    const snapshot = await get(commentsRef);

    if (!snapshot.exists()) {
      return {};
    }

    const allCommentsData = snapshot.val();
    const allComments: Record<string, Record<string, ICommentInfo[]>> = {};

    for (const [noteId, commentData] of Object.entries(allCommentsData)) {
      if (commentData && typeof commentData === 'object') {
        const comments: ICommentInfo[] = [];

        for (const [commentId, comment] of Object.entries(commentData)) {
          const commentInfo = comment as ICommentInfo;
          comments.push({
            ...commentInfo,
            id: commentId
          });
        }

        // Sort comments by creation date (newest first)
        const sortedComments = comments.sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

        // Find the airport/ICAO code for this note
        // We need to iterate through all notes to find which airport this note belongs to
        const allNotesRef = ref(REALTIME_DB, `/airport_notes/${airline}/notes`);
        const notesSnapshot = await get(allNotesRef);

        if (notesSnapshot.exists()) {
          const notesData = notesSnapshot.val();
          for (const [icaoCode, airportNotes] of Object.entries(notesData)) {
            if (
              airportNotes &&
              typeof airportNotes === 'object' &&
              noteId in airportNotes
            ) {
              if (!allComments[icaoCode]) {
                allComments[icaoCode] = {};
              }
              allComments[icaoCode][noteId] = sortedComments;
              break;
            }
          }
        }
      }
    }

    return allComments;
  } catch (error) {
    console.error('Error getting all comments for all airports:', error);
    throw error;
  }
};

/**
 * Validates and updates the comment count for a note if it's incorrect
 * @param icaoCode - Airport ICAO code
 * @param noteId - Note ID to validate
 * @returns Promise<void>
 */
export const validateAndUpdateCommentCount = async (
  icaoCode: string,
  noteId: string
): Promise<void> => {
  try {
    const airline = store.getState().user.airline;
    // Get current note data
    const noteRef = ref(
      REALTIME_DB,
      `/airport_notes/${airline}/notes/${icaoCode}/${noteId}`
    );
    const noteSnapshot = await get(noteRef);

    if (!noteSnapshot.exists()) {
      console.warn('Note not found:', noteId);
      return;
    }

    const noteData = noteSnapshot.val() as INoteInfo;
    const currentCommentCount = noteData.comment_count || 0;

    // Get actual comments count
    const comments = await getCommentsByNoteId(noteId);
    const actualCommentCount = comments.length;

    // Compare and update if different
    if (currentCommentCount !== actualCommentCount) {
      await update(noteRef, {comment_count: actualCommentCount});
      console.log(
        `Comment count updated for note ${noteId}: ${currentCommentCount} -> ${actualCommentCount}`
      );
    }
  } catch (error) {
    console.error('Error validating comment count:', error);
    throw error;
  }
};
